import { Box, Link, Typography } from "@mui/material";
import {
  BlocksRenderer,
  type BlocksContent,
} from "@strapi/blocks-react-renderer";
import type { StrapiBlock } from "api";

type Props = {
  content: BlocksContent | StrapiBlock[] | null | undefined;
};

export const TextBlocksRenderer = ({ content }: Props) => {
  content = content as BlocksContent;

  if (content === null || content === undefined || content.length === 0) {
    return null;
  }

  const cleanedContent = content.map((block) => {
    const textNodes = block.children.filter(
      (child) => (child as { text?: string }).text !== undefined,
    );
    if (
      block.type == "paragraph" &&
      textNodes.every(
        (textNode) => (textNode as { text: string }).text?.trim() === "",
      )
    ) {
      return {
        ...block,
        children: block.children.map((child, i) => {
          if (i === 0) return { ...child, text: " " };
          return child;
        }),
      };
    }
    return block;
  });

  return (
    <Box
      className="test"
      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
    >
      <BlocksRenderer
        content={cleanedContent}
        modifiers={{
          underline: ({ children }) => (
            <Typography
              variant="textBlock"
              component="span"
              sx={{ textDecoration: "underline" }}
              fontSize={"inherit"}
            >
              {children}
            </Typography>
          ),
          bold: ({ children }) => (
            <Typography
              variant="textBlock"
              component="span"
              sx={{ fontWeight: "bold" }}
              fontSize={"inherit"}
            >
              {children}
            </Typography>
          ),
          italic: ({ children }) => (
            <Typography
              variant="textBlock"
              component="span"
              sx={{ fontStyle: "italic" }}
              fontSize={"inherit"}
            >
              {children}
            </Typography>
          ),
          code: ({ children }) => (
            <Typography
              variant="textBlock"
              component="pre"
              fontSize={"inherit"}
              sx={{
                display: "inline-block",
                fontFamily: "monospace",
                backgroundColor: "rgba(100,100,100,0.3)",
                borderRadius: 1,
                p: "2px",
              }}
            >
              {children}
            </Typography>
          ),
          strikethrough: ({ children }) => (
            <Typography
              variant="textBlock"
              component="span"
              sx={{ textDecoration: "line-through" }}
              fontSize={"inherit"}
            >
              {children}
            </Typography>
          ),
        }}
        blocks={{
          paragraph: ({ children }) =>
            Array.isArray(children) &&
            children?.at(0)?.props?.text.trim() === "" ? (
              <Typography
                variant="textBlock"
                sx={{ whiteSpace: "pre-line" }}
                component="div"
              >
                {"\n"}
              </Typography>
            ) : (
              <Typography component="div" variant="body1">
                {children}
              </Typography>
            ),
          heading: ({ level, children }) => {
            if (
              Array.isArray(children) &&
              children?.at(0)?.props?.text.trim() === ""
            )
              return (
                <Typography
                  variant="textBlock"
                  sx={{ whiteSpace: "pre-line" }}
                  component="div"
                >
                  {"\n"}
                </Typography>
              );
            switch (level) {
              case 1:
                return (
                  <Typography component="h1" variant="h1">
                    {children}
                  </Typography>
                );
              case 2:
                return (
                  <Typography component="h2" variant="h2">
                    {children}
                  </Typography>
                );
              case 3:
                return (
                  <Typography component="h3" variant="h3">
                    {children}
                  </Typography>
                );
              case 4:
                return (
                  <Typography component="h4" variant="h4">
                    {children}
                  </Typography>
                );
              case 5:
                return (
                  <Typography component="h5" variant="h5">
                    {children}
                  </Typography>
                );
              case 6:
                return (
                  <Typography component="h6" variant="h6">
                    {children}
                  </Typography>
                );
              default:
                return (
                  <Typography component="h6" variant="h6">
                    {children}
                  </Typography>
                );
            }
          },
          link: ({ children, url }) => {
            if (
              Array.isArray(children) &&
              children?.at(0)?.props?.text.trim() === ""
            )
              return (
                <Typography
                  sx={{ whiteSpace: "pre-line" }}
                  component="div"
                  variant="textBlock"
                >
                  {"\n"}
                </Typography>
              );
            return (
              <Link
                component="a"
                variant="body1"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {children}
              </Link>
            );
          },
          quote: ({ children }) => {
            if (
              Array.isArray(children) &&
              children?.at(0)?.props?.text.trim() === ""
            )
              return (
                <Typography
                  sx={{ whiteSpace: "pre-line" }}
                  component="div"
                  variant="textBlock"
                >
                  {"\n"}
                </Typography>
              );
            return (
              <Typography
                component="span"
                variant="textBlock"
                sx={{
                  fontStyle: "italic",
                  display: "block",
                  textAlign: "left",
                  borderLeft: "4px solid rgba(100,100,100,0.6)",
                  marginLeft: 0,
                  paddingLeft: 1,
                }}
              >
                &quot;{children}&quot;
              </Typography>
            );
          },
          code: ({ children }) => {
            if (
              Array.isArray(children) &&
              children?.at(0)?.props?.text.trim() === ""
            )
              return (
                <Typography
                  sx={{ whiteSpace: "pre-line" }}
                  component="div"
                  variant="textBlock"
                >
                  {"\n"}
                </Typography>
              );

            return (
              <Typography
                component="pre"
                variant="textBlock"
                sx={{
                  fontFamily: "monospace",
                  backgroundColor: "rgba(100,100,100,0.3)",
                  borderRadius: 1,
                  p: "2px",
                }}
              >
                {children}
              </Typography>
            );
          },
          image: ({ image }) => {
            return (
              <img
                src={image.url}
                alt={image.alternativeText ?? undefined}
                style={{ maxWidth: "100%" }}
              />
            );
          },
          list: ({ children, format }) => {
            return format === "ordered" ? (
              <ol>{children}</ol>
            ) : (
              <ul>{children}</ul>
            );
          },
          "list-item": ({ children }) => {
            return <li>{children}</li>;
          },
        }}
      />
    </Box>
  );
};
